import { useAccounts, useDeleteAccount, useSnackbar, useUpdateAccountPermission } from '@e2m-customer-portal/hooks';
import { PermissionId } from '@e2m-customer-portal/shared';
import { ConfirmationDialog } from '@e2m-customer-portal/ui';
import { Avatar, IconButton, Switch, Typography } from '@mui/material';
import clsx from 'clsx';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ArrowDownIcon, BinIcon, EditIcon } from '../../../assets/icons';
import { Account } from '../../../generated/graphql';
import { isGranted as checkGranted, getErrorTranslationKey } from '../../../utils';
import { getInitials } from '../../../utils/get-initials';
import { DialogAccount } from './UserDialog';

interface MarketLocationCellProps {
  account: DialogAccount;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}

function MarketLocationCell({
  account,
  open,
  onOpen,
  onClose,
}: Readonly<MarketLocationCellProps>): ReactElement<MarketLocationCellProps> {
  const [position, setPosition] = useState(0);
  const [showingExpandButton, setShowingExpandButton] = useState(false);
  const maloListRef = useRef<HTMLDivElement>(null);

  const handleWindowSizeChange = () =>
    setShowingExpandButton((maloListRef.current?.offsetWidth ?? 0) < (maloListRef.current?.scrollWidth ?? 1));

  useEffect(() => {
    handleWindowSizeChange();
    window.addEventListener('resize', handleWindowSizeChange);
    return () => window.removeEventListener('resize', handleWindowSizeChange);
  }, [account.marketLocations]);

  return (
    <div className="relative ml-5 flex items-center justify-between">
      {open ? (
        <>
          <div
            className={clsx(
              'absolute z-10 -m-4 max-h-[25vh] w-[calc(100%-(24px+0.5rem))] overflow-y-scroll bg-white pl-4 pr-10',
              [
                position > window.innerHeight / 2
                  ? 'bottom-[0.3125rem] rounded-t-lg pt-4 shadow-[0_-20px_20px_-15px_rgba(0,0,0,0.2)]'
                  : 'top-[0.3125rem] rounded-b-lg pb-4 shadow-[0_20px_20px_-15px_rgba(0,0,0,0.2)]',
              ],
            )}
          >
            {account.marketLocations
              ?.map((marketLocation) => marketLocation.externalName || marketLocation.name)
              .join(', ')}
          </div>
          <IconButton
            className="absolute right-0 top-0 -translate-y-4 rotate-180 p-1"
            style={{
              minWidth: '24px',
              zIndex: 11,
            }}
            onClick={onClose}
          >
            <ArrowDownIcon />
          </IconButton>
        </>
      ) : (
        <>
          <div ref={maloListRef} className="truncate">
            {account.marketLocations
              ?.map((marketLocation) => marketLocation.externalName || marketLocation.name)
              .join(', ')}
          </div>
          {showingExpandButton && (
            <IconButton
              className="p-1"
              style={{
                minWidth: '24px',
              }}
              onClick={(event: React.MouseEvent) => {
                setPosition(event.pageY);
                onOpen();
              }}
            >
              <ArrowDownIcon />
            </IconButton>
          )}
        </>
      )}
    </div>
  );
}

export interface UserListProps {
  onStartEditing?: (account: Account) => void;
}

export function UserList({ onStartEditing }: Readonly<UserListProps>): ReactElement<UserListProps> {
  const [t] = useTranslation();
  const { showError } = useSnackbar();

  const accounts = useAccounts();
  const updatePermission = useUpdateAccountPermission();
  const deleteAccount = useDeleteAccount();

  const [deletionProps, setDeletionProps] = useState<{
    user: Account | undefined;
    open: boolean;
  }>({
    user: undefined,
    open: false,
  });
  const [expandedAccount, setExpandedAccount] = useState<string>();

  const deleteUser = (email?: string): void => {
    if (email) {
      deleteAccount(email).catch((error) => showError(t(getErrorTranslationKey(error), { email })));
    }
  };

  if (!accounts.length) {
    return (
      <Typography className="italic" variant="body1">
        {t('SETTINGS_USER_MANAGEMENT_USER_LIST_EMPTY')}
      </Typography>
    );
  }

  return (
    <>
      <ConfirmationDialog
        title={t('SETTINGS_USER_MANAGEMENT_DELETE_USER_TITLE')}
        message={
          <Trans components={{ strong: <strong /> }} values={{ name: deletionProps.user?.name }}>
            SETTINGS_USER_MANAGEMENT_DELETE_USER_MESSAGE
          </Trans>
        }
        confirmActionLabel="SETTINGS_USER_MANAGEMENT_DELETE_USER_TITLE"
        confirmAction={() => deleteUser(deletionProps.user?.email)}
        open={deletionProps.open}
        onClose={() => setDeletionProps({ ...deletionProps, open: false })}
      />

      <table className="w-full border-separate border-spacing-y-2">
        <thead>
          <tr className="text-sm">
            <th>
              <div />
            </th>
            <th className="w-11/12 text-start sm:w-5/12 md:w-3/12 2xl:w-2/12">
              <div className="mb-1 ml-5">{t('SHARED_NAME')}</div>
            </th>
            <th className="w-1/12 text-start 2xl:w-fit">
              <div className="mb-1 ml-5">{t('SHARED_ROLE')}</div>
            </th>
            <th className="hidden w-6/12 text-start sm:table-cell md:w-4/12 2xl:w-2/12">
              <div className="mb-1 ml-5">{t('SHARED_EMAIL')}</div>
            </th>
            <th className="hidden w-4/12 text-start lg:table-cell 2xl:w-3/12">
              <div className="mb-1 ml-5">{t('SETTINGS_USER_MANAGEMENT_MARKET_LOCATIONS')}</div>
            </th>
            <th className="hidden w-[12.5%] text-start 2xl:table-cell">
              <div className="mb-1 ml-5">{t('SETTINGS_SALES_PARTNER_SEE_FORECAST')}</div>
            </th>
            <th className="hidden w-[12.5%] text-start 2xl:table-cell">
              <div className="mb-1 ml-5">{t('SETTINGS_SALES_PARTNER_EDIT_FORECAST')}</div>
            </th>
            <th className="hidden w-[12.5%] text-start 2xl:table-cell">
              <div className="mb-1 ml-5">{t('SETTINGS_SALES_PARTNER_SEE_REVENUE')}</div>
            </th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((account) => {
            const isGranted = checkGranted(account.permissions);
            const isAdmin = isGranted(PermissionId.configureUsers) || isGranted(PermissionId.configureAdmins);

            return (
              <tr key={account.id}>
                <td className="w-fit bg-white sm:rounded-l-lg">
                  <div className="my-3 ml-2.5 sm:ml-5">
                    <Avatar className="size-[2.1875rem] bg-blue-grey-200 text-base font-semibold">
                      {getInitials(account.name)}
                    </Avatar>
                  </div>
                </td>
                <td className="w-11/12 max-w-0 bg-white sm:w-5/12 md:w-3/12 2xl:w-2/12">
                  <div className="ml-5 truncate">{account.name}</div>
                </td>
                <td className="w-1/12 bg-white 2xl:w-fit">
                  <div className="ml-5">{isAdmin ? t('ROLE_ADMIN') : t('ROLE_USER')}</div>
                </td>
                <td className="hidden w-6/12 max-w-0 bg-white sm:table-cell md:w-4/12 2xl:w-2/12">
                  <div className="ml-5 truncate">{account.email}</div>
                </td>
                <td className="hidden w-4/12 max-w-0 bg-white text-start lg:table-cell 2xl:w-3/12">
                  {!isGranted(PermissionId.configureUsers) && (
                    <MarketLocationCell
                      account={account}
                      open={account.email === expandedAccount}
                      onOpen={() => setExpandedAccount(account.email)}
                      onClose={() => setExpandedAccount(undefined)}
                    />
                  )}
                </td>
                <td className="hidden w-[12.5%] max-w-0 bg-white text-start 2xl:table-cell">
                  <div className={clsx(['ml-5 flex items-center gap-1.5 lowercase', { 'opacity-50': isAdmin }])}>
                    {t('SHARED_NO')}
                    <Switch
                      checked={isAdmin || isGranted(PermissionId.seeForecast)}
                      disabled={isAdmin}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        updatePermission(account.email, PermissionId.seeForecast, event.target.checked).catch((error) =>
                          showError(t(getErrorTranslationKey(error))),
                        );
                        if (!event.target.checked) {
                          updatePermission(account.email, PermissionId.editForecast, false).catch((error) =>
                            showError(t(getErrorTranslationKey(error), { email: account.email })),
                          );
                        }
                      }}
                    />
                    {t('SHARED_YES')}
                  </div>
                </td>
                <td className="hidden w-[12.5%] max-w-0 bg-white text-start 2xl:table-cell">
                  <div className={clsx(['ml-5 flex items-center gap-1.5 lowercase', { 'opacity-50': isAdmin }])}>
                    {t('SHARED_NO')}
                    <Switch
                      checked={isAdmin || isGranted(PermissionId.editForecast)}
                      disabled={isAdmin}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        updatePermission(account.email, PermissionId.editForecast, event.target.checked).catch(
                          (error) => showError(t(getErrorTranslationKey(error), { email: account.email })),
                        );
                        if (event.target.checked) {
                          updatePermission(account.email, PermissionId.seeForecast, true).catch((error) =>
                            showError(t(getErrorTranslationKey(error), { email: account.email })),
                          );
                        }
                      }}
                    />
                    {t('SHARED_YES')}
                  </div>
                </td>
                <td className="hidden w-[12.5%] max-w-0 bg-white text-start 2xl:table-cell">
                  <div className={clsx(['ml-5 flex items-center gap-1.5 lowercase', { 'opacity-50': isAdmin }])}>
                    {t('SHARED_NO')}
                    <Switch
                      checked={isAdmin || isGranted(PermissionId.seeRevenue)}
                      disabled={isAdmin}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        updatePermission(account.email, PermissionId.seeRevenue, event.target.checked).catch((error) =>
                          showError(t(getErrorTranslationKey(error), { email: account.email })),
                        )
                      }
                    />
                    {t('SHARED_YES')}
                  </div>
                </td>
                <td className="bg-white sm:rounded-r-lg">
                  <div className="mx-2.5 flex items-center justify-end sm:mx-5">
                    <IconButton
                      className="text-black"
                      title={t('SETTINGS_USER_MANAGEMENT_EDIT_USER_TITLE')}
                      onClick={onStartEditing && (() => onStartEditing(account))}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      className="text-black"
                      title={t('SETTINGS_USER_MANAGEMENT_DELETE_USER_TITLE')}
                      onClick={() => {
                        setDeletionProps({ user: account, open: true });
                      }}
                    >
                      <BinIcon />
                    </IconButton>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}
